import React from 'react';
import PropTypes from 'prop-types';
import { ChevronDown } from 'lucide-react';
import './ProductColorSelector.css';

/**
 * ProductColorSelector — Configurator
 *
 * Compact trigger showing the product's current colour: a 32px swatch, the
 * colour name and a chevron. It opens whatever colour picker the page wires
 * to `onClick` (pass `aria-expanded` / `aria-haspopup` through as needed).
 *
 * Figma component set: 4277:23148
 * Variants: State = Default (4277:23147) | Hover (4277:23146)
 *
 * Hover is CSS-only. `compact` is the mobile trigger the OVER/LL configurator
 * draws (1878:51298, an instance override — no library variant): 52×36 on
 * gray-1, 28px swatch, chevron only. The name then becomes the aria-label.
 */
const ProductColorSelector = ({
  color = 'var(--color-gray-0)',
  name,
  compact = false,
  ariaLabel,
  onClick,
  className = '',
  ...props
}) => (
  <button
    type="button"
    className={['cfg-product-color', compact && 'cfg-product-color--compact', className].filter(Boolean).join(' ')}
    onClick={onClick}
    aria-label={ariaLabel || (compact ? name : undefined)}
    {...props}
  >
    <span className="cfg-product-color__swatch" aria-hidden="true">
      <span className="cfg-product-color__fill" style={{ backgroundColor: color }} />
    </span>
    <span className="cfg-product-color__label">
      {name && !compact && <span className="cfg-product-color__name typo-h6">{name}</span>}
      <ChevronDown className="cfg-product-color__chevron" size={16} aria-hidden="true" />
    </span>
  </button>
);

ProductColorSelector.propTypes = {
  /** CSS colour of the swatch (hex, rgb, var(--…)) */
  color: PropTypes.string,
  /** Colour name (typo-h6, uppercase); the aria-label when compact */
  name: PropTypes.string,
  /** Mobile trigger: 52×36 on gray-1, 28px swatch, chevron only */
  compact: PropTypes.bool,
  /** Accessible name — say what the button does ("Change colour, selected: White"); compact falls back to the colour name */
  ariaLabel: PropTypes.string,
  /** Click handler — open the colour picker */
  onClick: PropTypes.func,
  /** Additional CSS class */
  className: PropTypes.string,
};

export default ProductColorSelector;
